import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import Confetti from "react-confetti";
import { makeStyles } from "tss-react/mui";

import courseAssignedSvg from "../../../assets/svgs/courseAssigned.svg";
import arrowSvg from "../../../assets/svgs/arrow.svg";
import wellDoneBoySvg from "../../../assets/images/study/wellDoneBoy.svg";
import wellDoneGirlSvg from "../../../assets/images/study/wellDoneGirl.svg";
import { useProfile } from "../../../pages/profile/hooks";
import { ButtonYellow } from "../../buttons";

const useStyles = makeStyles()((theme) => ({
  container: {
    textAlign: "center",
    backgroundColor: "#f0f8ff",
    position: "relative",
    minHeight: "calc(100vh - 65px)",
    width: "auto",
    fontFamily: '"Quicksand", sans-serif',
  },
  header: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    top: "0",
  },
  headerImage: {
    width: "100%",
  },
  bgImage: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    display: "none",
    [theme.breakpoints.up("lg")]: {
      display: "block",
    },
  },
  content: {
    zIndex: 1,
    position: "relative",
    fontFamily: '"Quicksand", sans-serif!important',
    height: "100%",
  },
  sectionContent: {
    marginTop: "14%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  container2: {
    height: "calc(100vh - 90px)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "auto",
  },
  imageContainer: {
    width: "100%",
    flexShrink: 0,
  },
  textContainer: {
    textAlign: "center",
    padding: theme.spacing(3),
    width: "100%",
    maxWidth: "800px",
  },
  studentName: {
    marginBottom: theme.spacing(2),
    color: "rgba(255, 199, 0, 1)",
    fontWeight: "700",
    lineHeight: 1.2,
    fontSize: "4em",
    fontFamily: '"ms sans serif", "Segoe UI", Tahoma, sans-serif',
    position: "relative",
    textShadow: `
          0px 4px 16px rgba(0, 0, 0, 0.11),
          -1px -1px 0 white,
          1px -1px 0 white,
          -1px 1px 0 white,
          1px 1px 0 white,
          -2px -2px 0 white,
          2px -2px 0 white,
          -2px 2px 0 white,
          2px 2px 0 white,
          -3px -3px 0 white,
          3px -3px 0 white,
          -3px 3px 0 white,
          3px 3px 0 white,
          -4px -4px 0 white,
          4px -4px 0 white,
          -4px 4px 0 white,
          4px 4px 0 white
        `,
    [theme.breakpoints.down("xl")]: {
      fontSize: "2.5em",
    },
    [theme.breakpoints.down("lg")]: {
      fontSize: "2em",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.5em",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.2em",
    },
  },
  completeMess: {
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: "2em",
    [theme.breakpoints.down("lg")]: {
      fontSize: "1.5em",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.3em",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1em",
    },
  },
  completeMess2: {
    fontFamily: '"Carlito", "Quicksand", "Segoe UI", Arial, sans-serif',
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    fontSize: "1.4em",
    [theme.breakpoints.down("lg")]: {
      fontSize: "1.2em",
    },
    [theme.breakpoints.down("md")]: {
      fontSize: "1.1em",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "1em",
    },
  },
}));

const CourseAssigned = ({ course, onStartCourse }) => {
  const { classes } = useStyles();
  const { profile } = useProfile();
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [windowDimension, setWindowDimension] = React.useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimension({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    setShowConfetti(true);

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const user_full_name = useMemo(
    () => `${profile?.firstName} ${profile?.lastName}`,
    [profile],
  );

  return (
    <Box className={classes.container2}>
      {showConfetti && (
        <Confetti
          width={windowDimension.width}
          height={windowDimension.height}
          numberOfPieces={200}
          recycle={true}
          run={showConfetti}
          gravity={0.3}
          initialVelocityY={10}
          tweenDuration={10}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        />
      )}
      <Box className={classes.imageContainer}>
        <img
          style={{
            width: "100%",
            maxHeight: "20vh",
          }}
          src={courseAssignedSvg}
          alt="assigned"
        />
      </Box>

      <Box className={classes.textContainer}>
        <Typography className={classes.studentName} variant="h4">
          {user_full_name} - {profile?.grade}
        </Typography>
        <Typography className={classes.completeMess} variant="h6" gutterBottom>
          Congratulations! Your diagnostic is complete.
        </Typography>
        <Box>
          <img
            width={"30%"}
            src={
              profile?.gender === "FEMALE" ? wellDoneGirlSvg : wellDoneBoySvg
            }
            alt=""
          />
        </Box>
        <Box>
          <Typography
            className={classes.completeMess2}
            variant="h6"
            gutterBottom
          >
            {course?.courseName
              ? `You've been assigned to ${course.courseName}. This is your starting level.`
              : "A new level has been assigned to you. Ready to begin your learning journey?"}
          </Typography>
          <ButtonYellow
            sx={{
              marginTop: 2,
              width: "fit-content",
              padding: "3px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
            onClick={onStartCourse}
          >
            <Typography
              sx={{
                fontWeight: "700",
                fontFamily: '"Quicksand", sans-serif',
                fontSize: "0.85em",
                zIndex: 2,
              }}
            >
              Start
            </Typography>
            <img
              style={{
                width: "14px",
                marginLeft: "8px",
                height: "auto",
                zIndex: 2,
              }}
              src={arrowSvg}
              alt=""
            />
          </ButtonYellow>
        </Box>
      </Box>
    </Box>
  );
};

export default CourseAssigned;
